import {
  Button,
  Input,
  Field,
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarTitle
} from '@fluentui/react-components';
import { Drawer, DrawerBody, DrawerFooter, DrawerHeader, DrawerHeaderTitle } from '@fluentui/react-components/unstable';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../../../../redux/reducers';
import { ErrorMessageComponent } from '../../../../Error/ErrorMessageComponent';

// i18Next
import { useTranslation, Trans } from 'react-i18next';

// store
import * as ConceptCategoryStore from '../../../../../redux/middleware/ConceptCategoryMiddleware';
import {
  ContractConceptCategoryFormProps,
  FormValidation,
  buildCreatePayload,
  buildUpdatePayload,
  validateForm
} from './ContractConceptCategory.Form.Functions';
import { useContractConceptCategoryFormStyles } from './ContractConceptCategory.Form.Styles';

export const ContractConceptCategoryFormComponent = (props: ContractConceptCategoryFormProps) => {
  const [submittedForm, setSubmittedForm] = useState<boolean>(false);
  const [formValidationStatus, setFormValidationStatus] = useState<FormValidation | undefined>();

  const dispatch = useDispatch();
  const [t] = useTranslation();
  const styles = useContractConceptCategoryFormStyles();

  const conceptCategoryState = useSelector((appState: AppState) => appState.ConceptCategories);

  const [conceptCategoryName, setConceptCategoryName] = useState<string>('');
  const [conceptCategoryDescription, setConceptCategoryDescription] = useState<string>('');

  useEffect(() => {
    if (props.ConceptCategory) {
      setConceptCategoryName(props.ConceptCategory.conceptCategoryName || '');
      setConceptCategoryDescription(props.ConceptCategory.conceptCategoryDescription || '');
    } else {
      resetForm();
    }
  }, [props.ConceptCategory]);

  useEffect(() => {
    if (
      submittedForm &&
      conceptCategoryState &&
      (conceptCategoryState.successAddingNewOne || conceptCategoryState.successUpdatingOne)
    ) {
      setTimeout(() => {
        resetForm();
        props.dismissPanel();
      }, 100);
    }
  }, [
    submittedForm,
    conceptCategoryState?.successAddingNewOne,
    conceptCategoryState?.successUpdatingOne,
    props.dismissPanel
  ]);

  const resetForm = () => {
    setConceptCategoryName('');
    setConceptCategoryDescription('');
    setSubmittedForm(false);
    setFormValidationStatus(undefined);
  };

  const onChangeConceptCategoryName = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setConceptCategoryName(ev.target.value);
  };

  const onChangeConceptCategoryDescription = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setConceptCategoryDescription(ev.target.value);
  };

  const handleSaveForm = () => {
    setSubmittedForm(true);

    const validation = validateForm(
      {
        conceptCategoryName,
        conceptCategoryDescription
      },
      t
    );

    setFormValidationStatus(validation);

    if (!validation.success) {
      return;
    }

    if (props.ConceptCategory) {
      const updatedCategory = buildUpdatePayload(
        props.ConceptCategory,
        conceptCategoryName,
        conceptCategoryDescription
      );
      dispatch(ConceptCategoryStore.actionCreators.UpdateConceptCategory(updatedCategory));
    } else {
      const newCategory = buildCreatePayload(
        conceptCategoryName,
        conceptCategoryDescription
      );
      dispatch(ConceptCategoryStore.actionCreators.AddConceptCategory(newCategory));
    }
  };

  const handleCancelForm = () => {
    resetForm();
    props.dismissPanel();
  };

  return (
    <Drawer
      open={props.isOpen}
      onOpenChange={(_, { open }) => !open && props.dismissPanel()}
      position="end"
      size="small"
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              aria-label="Close"
              icon={<Dismiss24Regular />}
              onClick={props.dismissPanel}
            />
          }
        >
          {props.ConceptCategory
            ? t('contractConceptCategoryForm.titleEdit')
            : t('contractConceptCategoryForm.title')}
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody>
        <div className={styles.container}>
          {submittedForm && !formValidationStatus?.success && (
            <MessageBar intent="error">
              <MessageBarBody>
                <MessageBarTitle>{t('contractConceptCategoryForm.reviewInformation')}</MessageBarTitle>
                <ul>
                  {formValidationStatus?.errors.map((error: string) => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
              </MessageBarBody>
            </MessageBar>
          )}

          {conceptCategoryState &&
            (conceptCategoryState.failOnAddingNewOne || conceptCategoryState.failOnUpdatingOne) &&
            conceptCategoryState.error && (
              <ErrorMessageComponent Error={conceptCategoryState.error} />
            )}

          <div className={styles.formContainer}>
            <Field
              label={t('contractConceptCategoryForm.fields.name')}
              required
              validationMessage={
                submittedForm && (!conceptCategoryName || conceptCategoryName.trim() === '')
                  ? t('contractConceptCategoryForm.validation.requiredName')
                  : undefined
              }
            >
              <Input
                name="txt_concept_category_name"
                onChange={onChangeConceptCategoryName}
                value={conceptCategoryName}
                placeholder={t('contractConceptCategoryForm.fields.namePlaceholder')}
              />
            </Field>

            <Field label={t('contractConceptCategoryForm.fields.description')}>
              <Input
                name="txt_concept_category_description"
                onChange={onChangeConceptCategoryDescription}
                value={conceptCategoryDescription}
                placeholder={t('contractConceptCategoryForm.fields.descriptionPlaceholder')}
              />
            </Field>
          </div>
        </div>
      </DrawerBody>

      <DrawerFooter>
        <Button onClick={handleCancelForm}>
          <Trans i18nKey={'contractConceptCategoryForm.cancelButton'} />
        </Button>
        <Button
          appearance="primary"
          onClick={handleSaveForm}
          disabled={conceptCategoryState?.isAddingNewOne || conceptCategoryState?.isUpdatingOne}
        >
          {conceptCategoryState?.isAddingNewOne || conceptCategoryState?.isUpdatingOne ? (
            <Spinner size="tiny" />
          ) : (
            <Trans i18nKey={'contractConceptCategoryForm.saveButton'} />
          )}
        </Button>
      </DrawerFooter>
    </Drawer>
  );
};

export default ContractConceptCategoryFormComponent;
